import { useState, useEffect } from 'react';
import { groundsAPI } from '../../services/api';
import { PageHeader, SectionTitle, Field, DataTable, StatusBadge, Modal, ConfirmDialog, Spinner, EmptyState } from '../../components/ui';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

const EMPTY_FORM = {
  name: '', short_name: '', address: '', city: '', state: 'MI',
  google_maps_url: '', ground_type: 'standard', max_concurrent: '1',
  cricclubs_id: ''
};

export default function GroundsPage() {
  const { canEdit, isAdmin } = useAuthStore();
  const [grounds, setGrounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => { loadGrounds(); }, []);

  const loadGrounds = async () => {
    setLoading(true);
    try {
      const { data } = await groundsAPI.list();
      setGrounds(data);
    } catch { toast.error('Failed to load grounds'); }
    finally { setLoading(false); }
  };

  const setField = (key, value) => setForm(p => ({ ...p, [key]: value }));

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (g) => {
    setForm({
      name: g.name, short_name: g.short_name || '',
      address: g.address || '', city: g.city || '', state: g.state || 'MI',
      google_maps_url: g.google_maps_url || '',
      ground_type: g.ground_type || 'standard',
      max_concurrent: String(g.max_concurrent || 1),
      cricclubs_id: g.cricclubs_id || ''
    });
    setEditingId(g.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name) return toast.error('Ground name is required');
    if (!form.address) return toast.error('Address is required');
    setSaving(true);
    try {
      const payload = { ...form, max_concurrent: parseInt(form.max_concurrent) || 1, cricclubs_id: form.cricclubs_id || null };
      if (editingId) { await groundsAPI.update(editingId, payload); toast.success('Ground updated'); }
      else { await groundsAPI.create(payload); toast.success('Ground created'); }
      setShowForm(false);
      loadGrounds();
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await groundsAPI.delete(showDeleteConfirm);
      toast.success('Ground deleted');
      setShowDeleteConfirm(null);
      loadGrounds();
    } catch (err) { toast.error(err.response?.data?.error || 'Delete failed'); }
  };

  const filtered = grounds.filter(g =>
    !search || g.name.toLowerCase().includes(search.toLowerCase()) || g.city?.toLowerCase().includes(search.toLowerCase())
  );

  const premierCount = grounds.filter(g => g.ground_type === 'premier').length;
  const linkedCount = grounds.filter(g => g.cricclubs_id).length;
  const totalPitches = grounds.reduce((a, g) => a + (parseInt(g.max_concurrent) || 1), 0);

  return (
    <div>
      <PageHeader icon="⬕" title="Grounds"
        subtitle="Cricket grounds, addresses, pitch capacity and CricClubs links."
        actions={canEdit() && <button className="btn btn-primary" onClick={openCreate}>+ Add Ground</button>}
      />

      {/* Stats */}
      <div className="grid-4" style={{ marginBottom: 20 }}>
        {[
          { label: 'Total Grounds', value: grounds.length, sub: 'Registered', color: 'var(--accent)' },
          { label: 'Premier', value: premierCount, sub: 'Premier grounds', color: 'var(--gold)' },
          { label: 'CricClubs Linked', value: linkedCount, sub: `${grounds.length - linkedCount} not linked`, color: 'var(--blue)' },
          { label: 'Total Pitches', value: totalPitches, sub: 'Concurrent capacity', color: 'var(--purple)' },
        ].map(s => (
          <div key={s.label} className="card stat-card">
            <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Search + Table */}
      <div className="card" style={{ padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <SectionTitle>All Grounds</SectionTitle>
          <input className="input" style={{ width: 220 }} placeholder="Search grounds..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner size={28} /></div>
        ) : filtered.length === 0 ? (
          <EmptyState icon="⬕" title="No grounds yet"
            description="Add your first cricket ground to get started."
            action={canEdit() && <button className="btn btn-primary" onClick={openCreate}>+ Add Ground</button>}
          />
        ) : (
          <DataTable
            headers={['#', 'Ground', 'City', 'Type', 'Pitches', 'Address', 'CricClubs', ...(canEdit() ? ['Actions'] : [])]}
            rows={filtered.map((g, i) => [
              <span className="mono" style={{ color: 'var(--mid)', fontSize: 11 }}>{i + 1}</span>,
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{g.name}</div>
                {g.short_name && <div className="mono" style={{ fontSize: 10, color: 'var(--accent)', marginTop: 1 }}>{g.short_name}</div>}
              </div>,
              <span style={{ fontSize: 12 }}>{g.city || '—'}{g.state ? `, ${g.state}` : ''}</span>,
              g.ground_type === 'premier'
                ? <span className="tag tag-gold">Premier</span>
                : <span className="tag tag-blue">Standard</span>,
              <span className="mono" style={{ fontWeight: 700, color: 'var(--accent)' }}>{g.max_concurrent || 1}</span>,
              <div style={{ fontSize: 11, color: 'var(--mid)', maxWidth: 180 }}>
                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.address || '—'}</div>
                {g.google_maps_url && (
                  <a href={g.google_maps_url} target="_blank" rel="noreferrer"
                    style={{ color: 'var(--blue)', fontSize: 10, textDecoration: 'none' }}>📍 View Map</a>
                )}
              </div>,
              g.cricclubs_id
                ? <span className="tag tag-green">Linked · {g.cricclubs_id}</span>
                : <span className="tag tag-mid">Not linked</span>,
              ...(canEdit() ? [
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(g)}>Edit</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => window.location.href = `/availability?ground=${g.id}`}>Availability</button>
                  {isAdmin() && <button className="btn btn-danger btn-sm" onClick={() => setShowDeleteConfirm(g.id)}>Delete</button>}
                </div>
              ] : [])
            ])}
          />
        )}
      </div>

      {/* Ground cards for quick reference */}
      {!loading && grounds.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, marginTop: 16 }}>
          {grounds.map(g => (
            <div key={g.id} className="card" style={{ padding: 14, borderLeft: `3px solid ${g.ground_type === 'premier' ? 'var(--gold)' : 'var(--blue)'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{g.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--mid)', marginTop: 3 }}>{g.city}{g.state ? `, ${g.state}` : ''}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <span className={`tag ${g.ground_type === 'premier' ? 'tag-gold' : 'tag-blue'}`}>{g.ground_type === 'premier' ? 'Premier' : 'Standard'}</span>
                  <span style={{ fontSize: 10, color: 'var(--mid)' }}>{g.max_concurrent || 1} pitch{(g.max_concurrent || 1) > 1 ? 'es' : ''}</span>
                </div>
              </div>
              {g.google_maps_url && (
                <a href={g.google_maps_url} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-block', marginTop: 8, fontSize: 11, color: 'var(--blue)', textDecoration: 'none' }}>
                  📍 View on Maps
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <Modal title={editingId ? 'Edit Ground' : 'Add Ground'} onClose={() => setShowForm(false)} width={580}>
          <Field label="Ground Name" required>
            <input className="input" placeholder="e.g. Lasky Recreation Center" value={form.name} onChange={e => setField('name', e.target.value)} />
          </Field>

          <Field label="Short Name">
            <input className="input" placeholder="e.g. Lasky" value={form.short_name} onChange={e => setField('short_name', e.target.value)} />
          </Field>

          <Field label="Address" required>
            <input className="input" placeholder="Street address" value={form.address} onChange={e => setField('address', e.target.value)} />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 12 }}>
            <Field label="City">
              <input className="input" placeholder="Detroit" value={form.city} onChange={e => setField('city', e.target.value)} />
            </Field>
            <Field label="State">
              <input className="input" placeholder="MI" value={form.state} onChange={e => setField('state', e.target.value)} />
            </Field>
          </div>

          <Field label="Google Maps Link" note="Optional — paste full URL">
            <input className="input" placeholder="https://maps.google.com/..." value={form.google_maps_url} onChange={e => setField('google_maps_url', e.target.value)} />
          </Field>

          <Field label="Ground Type">
            <div style={{ display: 'flex', gap: 10 }}>
              {[{ value: 'premier', label: 'Premier', color: 'var(--gold)' }, { value: 'standard', label: 'Standard', color: 'var(--blue)' }].map(opt => (
                <div key={opt.value} onClick={() => setField('ground_type', opt.value)}
                  style={{ flex: 1, padding: '10px 14px', borderRadius: 7, cursor: 'pointer', border: `1.5px solid ${form.ground_type === opt.value ? opt.color : 'var(--border)'}`, background: form.ground_type === opt.value ? `rgba(${opt.color === 'var(--gold)' ? '245,166,35' : '59,130,246'},.08)` : 'transparent', display: 'flex', alignItems: 'center', gap: 8, transition: 'all .15s' }}>
                  <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${form.ground_type === opt.value ? opt.color : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {form.ground_type === opt.value && <div style={{ width: 6, height: 6, borderRadius: '50%', background: opt.color }} />}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: form.ground_type === opt.value ? opt.color : 'var(--text)' }}>{opt.label}</span>
                </div>
              ))}
            </div>
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Max Concurrent Games" note="How many matches can run simultaneously">
              <div style={{ display: 'flex', gap: 6 }}>
                {['1', '2', '3', '4'].map(n => (
                  <button key={n} type="button"
                    className={`btn btn-sm ${form.max_concurrent === n ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setField('max_concurrent', n)}>{n}</button>
                ))}
              </div>
            </Field>
            <Field label="CricClubs Ground ID" note="Optional">
              <input className="input mono" placeholder="e.g. 44" value={form.cricclubs_id} onChange={e => setField('cricclubs_id', e.target.value)} />
            </Field>
          </div>

          <hr className="divider" />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><Spinner size={14} /> Saving...</> : editingId ? '✓ Update Ground' : '+ Add Ground'}
            </button>
          </div>
        </Modal>
      )}

      {showDeleteConfirm && (
        <ConfirmDialog danger
          message="Delete this ground? Ground availability records will also be removed. This cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(null)}
        />
      )}
    </div>
  );
}